import React, { useEffect } from 'react';
import { ArrowLeft, CreditCard, Download, HeartPulse, Mail, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const details = [
  {
    icon: Download,
    title: 'Digital delivery',
    text: 'Book purchases are delivered digitally. After Stripe verifies payment, the return page provides the files included with the selected offer and the book-code app instructions.',
  },
  {
    icon: CreditCard,
    title: 'Payment handling',
    text: 'Stripe processes the checkout and payment details. Integrative Health Journal receives the order information needed to verify the purchase and deliver the files, but does not store full payment-card details.',
  },
  {
    icon: ShieldCheck,
    title: 'App access',
    text: 'When app access is included, the delivery page and purchase email provide the book-code path. Keep access codes private and use the email entered at checkout when requesting support.',
  },
  {
    icon: HeartPulse,
    title: 'Educational use',
    text: 'The books and nutrition app provide general educational information and tracking tools. They are not medical advice and do not diagnose, prevent, or treat medical conditions.',
  },
];

const BookPurchaseDetailsPage = () => {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'Book Purchase, Delivery & Privacy Details | Integrative Health Journal';
    return () => {
      document.title = originalTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f4ec] text-[#20251f]">
      <header className="border-b border-[#ddd6c7] bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/books/macros-made-easy-for-women" className="inline-flex items-center gap-2 text-sm font-black text-[#34412f] hover:text-[#1f2c1e]">
            <ArrowLeft className="h-4 w-4" />
            Back to Macros Made Easy
          </Link>
          <span className="text-sm font-black text-[#526044]">Integrative Health Journal</span>
        </div>
      </header>

      <main className="px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-5xl">
          <p className="text-sm font-black uppercase tracking-[0.2em] text-[#687650]">Before checkout</p>
          <h1 className="mt-3 max-w-4xl text-4xl font-black leading-tight text-[#243021] sm:text-5xl">Purchase, delivery, and privacy details.</h1>
          <p className="mt-5 max-w-3xl text-lg leading-8 text-[#596250]">
            A plain-language summary of what happens when you purchase a digital book from Integrative Health Journal.
          </p>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {details.map(({ icon: Icon, title, text }) => (
              <section key={title} className="rounded-lg border border-[#ddd6c7] bg-white p-6 shadow-sm">
                <Icon className="h-7 w-7 text-[#536343]" />
                <h2 className="mt-4 text-xl font-black text-[#243021]">{title}</h2>
                <p className="mt-3 text-sm leading-7 text-[#596250]">{text}</p>
              </section>
            ))}
          </div>

          <section className="mt-8 rounded-lg border border-[#cbd5bd] bg-[#f1f6ea] p-6">
            <h2 className="text-xl font-black text-[#243021]">Advertising measurement</h2>
            <p className="mt-3 text-sm leading-7 text-[#4e5b47]">
              The book sales pages use the Meta Pixel to measure standard events such as viewing a product, beginning checkout, and completing a purchase. Meta may use cookies or browser identifiers according to its own settings and policies. Purchase events contain order details needed for advertising measurement, not full card information.
            </p>
          </section>

          <section className="mt-8 flex flex-col gap-4 rounded-lg bg-[#243021] p-6 text-white sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-black">Questions about a purchase?</h2>
              <p className="mt-2 text-sm leading-6 text-[#d8dfd3]">Include the email used at checkout so the order can be located.</p>
            </div>
            <a href="mailto:info@integrativehealthjournal.com?subject=Book%20purchase%20question" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-black text-[#243021] hover:bg-[#eef3e9]">
              <Mail className="h-4 w-4" />
              Email purchase support
            </a>
          </section>
        </div>
      </main>
    </div>
  );
};

export default BookPurchaseDetailsPage;
